"use client";

import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

type ThemeMode = 'dark' | 'light';

const ThemeToggle: React.FC = () => {
  const [theme, setTheme] = useState<ThemeMode>('dark');
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const storedTheme = window.localStorage.getItem('theme') as ThemeMode | null;
    const initialTheme = storedTheme ?? 'dark';

    setTheme(initialTheme);
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(initialTheme);
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;

    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(theme);
    window.localStorage.setItem('theme', theme);
  }, [theme, isMounted]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const nextTheme = theme === 'dark' ? 'light' : 'dark';

  return (
    <button data-component-id="src/components/ThemeToggle.tsx:37:4" data-component-path="src/components/ThemeToggle.tsx" data-component-line="37" data-component-end-line="60" data-component-file="ThemeToggle.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22inline-flex%20items-center%20gap-2%20rounded-full%20px-4%20py-2%20glass-card%20text-sm%20font-semibold%20text-foreground%20hover%3Atext-primary%20transition-transform%20duration-300%20hover%3Ascale-105%20focus-visible%3Aoutline-none%20focus-visible%3Aring-2%20focus-visible%3Aring-primary%2F70%20disabled%3Aopacity-70%22%7D"
      type="button"
      onClick={toggleTheme}
      aria-label={`Switch to ${nextTheme} mode`}
      title={`Switch to ${nextTheme} mode`}
      disabled={!isMounted}
      className="inline-flex items-center gap-2 rounded-full px-4 py-2 glass-card text-sm font-semibold text-foreground hover:text-primary transition-transform duration-300 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 disabled:opacity-70"
    >
      {isMounted ? (
        theme === 'dark' ? (
          <>
            <Icon data-component-id="src/components/ThemeToggle.tsx:48:12" data-component-path="src/components/ThemeToggle.tsx" data-component-line="48" data-component-end-line="48" data-component-file="ThemeToggle.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22SunIcon%22%2C%22className%22%3A%22text-amber-300%22%7D" name="SunIcon" size={18} variant="outline" className="text-amber-300" />
            <span data-component-id="src/components/ThemeToggle.tsx:49:12" data-component-path="src/components/ThemeToggle.tsx" data-component-line="49" data-component-end-line="49" data-component-file="ThemeToggle.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22hidden%20sm%3Ainline%22%2C%22textContent%22%3A%22Light%22%7D" className="hidden sm:inline">Light</span>
          </>
        ) : (
          <>
            <Icon data-component-id="src/components/ThemeToggle.tsx:53:12" data-component-path="src/components/ThemeToggle.tsx" data-component-line="53" data-component-end-line="53" data-component-file="ThemeToggle.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MoonIcon%22%2C%22className%22%3A%22text-indigo-500%22%7D" name="MoonIcon" size={18} variant="outline" className="text-indigo-500" />
            <span data-component-id="src/components/ThemeToggle.tsx:54:12" data-component-path="src/components/ThemeToggle.tsx" data-component-line="54" data-component-end-line="54" data-component-file="ThemeToggle.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22hidden%20sm%3Ainline%22%2C%22textContent%22%3A%22Dark%22%7D" className="hidden sm:inline">Dark</span>
          </>
        )
      ) : (
        <Icon data-component-id="src/components/ThemeToggle.tsx:58:8" data-component-path="src/components/ThemeToggle.tsx" data-component-line="58" data-component-end-line="58" data-component-file="ThemeToggle.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22NoSymbolIcon%22%2C%22className%22%3A%22animate-pulse%22%7D" name="NoSymbolIcon" size={18} variant="outline" className="animate-pulse" />
      )}
    </button>
  );
};

export default ThemeToggle;
